import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { AnimatePresence, motion } from "framer-motion"
import { Check, ImageOff, ImagePlus, Pencil, Plus, Search, Tags, Trash2, X } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"
import {
	createTaxonomy,
	deleteTaxonomy,
	listTaxonomy,
	renameTaxonomy,
	setTaxonomyIcon,
	type TaxonomyItem,
	type TaxonomyKind,
	uploadTaxonomyIcon,
} from "@/entities/category"
import { useSessionRole } from "@/entities/session"
import { useUrlState } from "@/shared/hooks/use-url-state"
import { fetchingDim, listItem, TRANSITION } from "@/shared/lib/motion"
import { useShownKey } from "@/shared/lib/use-shown-key"
import { cn } from "@/shared/lib/utils"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { EmptyState } from "@/shared/ui/empty-state"
import { Input } from "@/shared/ui/input"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { PageShell } from "@/shared/ui/page-shell"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Skeleton } from "@/shared/ui/skeleton"
import { SlidingPill } from "@/shared/ui/sliding-pill"

const KINDS: Array<{ value: TaxonomyKind; label: string; one: string; hint: string }> = [
	{
		value: "formats",
		label: "Форматы",
		one: "формат",
		hint: "Вид материала: видеоурок, эфир, разбор. Показываются иконками в каталоге.",
	},
	{
		value: "niches",
		label: "Ниши",
		one: "нишу",
		hint: "Сфера, для которой материал: недвижимость, психология, онлайн-школы.",
	},
	{
		value: "topics",
		label: "Темы",
		one: "тему",
		hint: "О чём материал: Reels, воронки, контент-план.",
	},
	{
		value: "durations",
		label: "Длительность",
		one: "длительность",
		hint: "Сколько времени займёт материал. У материала одно значение.",
	},
]

type Sort = "name" | "usage"

function plural(count: number): string {
	const mod10 = count % 10
	const mod100 = count % 100
	if (mod10 === 1 && mod100 !== 11) return "материал"
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "материала"
	return "материалов"
}

function TermRow({ item, kind }: { item: TaxonomyItem; kind: TaxonomyKind }) {
	const client = useQueryClient()
	const { confirm } = useConfirm()
	const input = useRef<HTMLInputElement>(null)
	const [editing, setEditing] = useState(false)
	const [name, setName] = useState(item.name)
	const refresh = () => client.invalidateQueries({ queryKey: [kind] })
	const onError = (fallback: string) => (error: unknown) =>
		toast.error(extractApiErrorMessage(error, fallback))

	const icon = useMutation({
		mutationFn: (file: File | null) =>
			file ? uploadTaxonomyIcon(kind, item, file) : setTaxonomyIcon(kind, item.id, null),
		onSuccess: refresh,
		onError: onError("Не удалось сохранить иконку"),
	})
	const rename = useMutation({
		mutationFn: () => renameTaxonomy(kind, item.id, name.trim()),
		onSuccess: async () => {
			setEditing(false)
			await refresh()
		},
		onError: onError("Не удалось переименовать"),
	})
	const remove = useMutation({
		mutationFn: () => deleteTaxonomy(kind, item.id),
		onSuccess: async () => {
			toast.success(`«${item.name}» удалено`)
			await refresh()
		},
		onError: onError("Не удалось удалить"),
	})
	const count = item.postCount ?? 0

	return (
		// Новое значение раскрывается, удалённое сворачивается по высоте, а при смене порядка
		// строки доезжают до новых мест.
		<motion.li
			layout="position"
			{...listItem}
			transition={{ layout: TRANSITION.slow }}
			className="group border-b border-border last:border-0"
		>
			<div className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
				<input
					ref={input}
					type="file"
					className="hidden"
					accept="image/jpeg,image/png,image/webp,image/svg+xml"
					aria-label={`Иконка: ${item.name}`}
					onChange={(event) => {
						const file = event.target.files?.[0]
						if (file) icon.mutate(file)
						event.target.value = ""
					}}
				/>
				<button
					type="button"
					onClick={() => input.current?.click()}
					title={item.iconUrl ? "Заменить иконку" : "Загрузить иконку"}
					className={cn(
						"relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-secondary transition-opacity",
						icon.isPending && "opacity-50",
					)}
				>
					{item.iconUrl ? (
						<img src={item.iconUrl} alt="" className="size-full object-cover" />
					) : (
						<ImagePlus className="size-4 text-muted-foreground" />
					)}
				</button>
				{editing ? (
					<form
						className="flex min-w-0 flex-1 items-center gap-2"
						onSubmit={(event) => {
							event.preventDefault()
							if (name.trim() && name.trim() !== item.name) rename.mutate()
							else setEditing(false)
						}}
					>
						<Input
							autoFocus
							placeholder="Название"
							value={name}
							onChange={(event) => setName(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Escape") {
									setName(item.name)
									setEditing(false)
								}
							}}
							className="h-10"
							aria-label="Новое название"
						/>
						<Button
							type="submit"
							size="icon"
							className="size-10 shrink-0"
							isLoading={rename.isPending}
							aria-label="Сохранить"
						>
							{rename.isPending ? null : <Check />}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="size-10 shrink-0"
							aria-label="Отмена"
							onClick={() => {
								setName(item.name)
								setEditing(false)
							}}
						>
							<X />
						</Button>
					</form>
				) : (
					<>
						<button
							type="button"
							onDoubleClick={() => setEditing(true)}
							className="min-w-0 flex-1 text-left"
							title="Дважды нажмите, чтобы переименовать"
						>
							<span className="block truncate font-medium">{item.name}</span>
							<span className="block text-xs text-muted-foreground tabular-nums">
								{count ? `${count} ${plural(count)}` : "не используется"}
							</span>
						</button>
						<RowActionsMenu
							label={`Действия: ${item.name}`}
							actions={[
								{ label: "Переименовать", icon: Pencil, onSelect: () => setEditing(true) },
								{
									label: item.iconUrl ? "Заменить иконку" : "Загрузить иконку",
									icon: ImagePlus,
									onSelect: () => input.current?.click(),
								},
								{
									label: "Убрать иконку",
									icon: ImageOff,
									hidden: !item.iconUrl,
									onSelect: () => icon.mutate(null),
								},
								{
									label: "Удалить",
									icon: Trash2,
									destructive: true,
									separatorBefore: true,
									onSelect: () =>
										confirm({
											title: `Удалить «${item.name}»?`,
											message: count
												? `Отметка пропадёт у ${count} ${plural(count)}, и в фильтрах каталога их станет не найти по ней.`
												: "Значение нигде не используется.",
											confirmText: "Удалить",
											onConfirm: () => remove.mutateAsync().then(() => undefined),
										}),
								},
							]}
						/>
					</>
				)}
			</div>
		</motion.li>
	)
}

/** Заглушка по форме списка: одна карточка, строки с иконкой, названием и числом материалов. */
function TermsSkeleton({ rows }: { rows: number }) {
	return (
		<ul aria-hidden="true" className="overflow-hidden rounded-2xl border border-border bg-card">
			{Array.from({ length: rows }, (_, index) => (
				<li
					key={index}
					className="flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-0 sm:px-4"
				>
					<Skeleton className="size-11 shrink-0 rounded-xl" />
					<div className="min-w-0 flex-1">
						<div className="flex h-6 items-center">
							<Skeleton className="h-4 rounded" style={{ width: `${46 - ((index * 11) % 24)}%` }} />
						</div>
						<div className="flex h-4 items-center">
							<Skeleton className="h-3 w-24 rounded" />
						</div>
					</div>
					<Skeleton className="size-8 shrink-0 rounded-full" />
				</li>
			))}
		</ul>
	)
}

export function TaxonomyPage() {
	const { isAdmin } = useSessionRole()
	const client = useQueryClient()
	const [kindParam, setKind] = useUrlState("kind", "formats")
	const kind = KINDS.find((item) => item.value === kindParam) ?? KINDS[0]!
	const [search, setSearch] = useState("")
	const [sort, setSort] = useState<Sort>("usage")
	const [draft, setDraft] = useState("")
	const query = useQuery({
		queryKey: [kind.value],
		queryFn: () => listTaxonomy(kind.value),
		enabled: isAdmin,
		placeholderData: (previous) => previous,
	})
	// Пока грузится другой классификатор, виден прежний список, а переход идёт, когда пришли
	// данные нового.
	const shownKind = useShownKey(kind.value, query.isPlaceholderData)
	const prefetch = (value: TaxonomyKind) =>
		void client.prefetchQuery({
			queryKey: [value],
			queryFn: () => listTaxonomy(value),
			staleTime: 30_000,
		})
	const create = useMutation({
		mutationFn: () => createTaxonomy(kind.value, draft.trim()),
		onSuccess: async (item) => {
			toast.success(`Добавлено: ${item.name}`)
			setDraft("")
			await client.invalidateQueries({ queryKey: [kind.value] })
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить")),
	})

	if (!isAdmin) return <AccessDenied title="Классификаторы" />

	const needle = search.trim().toLocaleLowerCase()
	const items = (query.data ?? [])
		.filter((item) => item.name.toLocaleLowerCase().includes(needle))
		.sort((left, right) =>
			sort === "usage"
				? (right.postCount ?? 0) - (left.postCount ?? 0) ||
					left.name.localeCompare(right.name, "ru")
				: left.name.localeCompare(right.name, "ru"),
		)
	const unused = (query.data ?? []).filter((item) => !item.postCount).length
	const noIcon = (query.data ?? []).filter((item) => !item.iconUrl).length

	return (
		<PageShell
			eyebrow="Каталог"
			title="Классификаторы"
			subtitle="Метки, по которым материалы ищут в каталоге приложений."
		>
			<div
				role="tablist"
				aria-label="Классификатор"
				className="hide-scrollbar relative mb-5 flex max-w-full gap-1 overflow-x-auto rounded-full bg-secondary/60 p-1 sm:w-fit"
			>
				<SlidingPill className="rounded-full" />
				{KINDS.map((item) => {
					const active = kind.value === item.value
					return (
						<button
							key={item.value}
							type="button"
							role="tab"
							aria-selected={active}
							data-active={active}
							onPointerEnter={() => prefetch(item.value)}
							onFocus={() => prefetch(item.value)}
							onClick={() => {
								setKind(item.value)
								setSearch("")
							}}
							className={cn(
								"relative shrink-0 rounded-full px-3.5 py-1.5 text-sm whitespace-nowrap transition-colors",
								active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
							)}
						>
							<span className="relative">{item.label}</span>
						</button>
					)
				})}
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
				<div className="min-w-0 space-y-3">
					<div className="flex flex-wrap gap-2">
						<div className="relative min-w-52 flex-1">
							<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								type="search"
								aria-label="Найти"
								placeholder={`Найти среди ${query.data?.length ?? 0}`}
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								className="h-11 pl-9"
							/>
						</div>
						<div
							className="relative flex rounded-xl bg-secondary/60 p-1 text-sm"
							role="group"
							aria-label="Порядок"
						>
							<SlidingPill className="rounded-lg" />
							{(
								[
									["usage", "Популярные"],
									["name", "По алфавиту"],
								] as const
							).map(([value, label]) => (
								<button
									key={value}
									type="button"
									aria-pressed={sort === value}
									data-active={sort === value}
									onClick={() => setSort(value)}
									className={cn(
										"relative rounded-lg px-3 transition-colors",
										sort === value
											? "text-foreground"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									{label}
								</button>
							))}
						</div>
					</div>

					<LoadSwap
						loading={query.isLoading}
						id={
							query.isError
								? "error"
								: items.length
									? `list-${shownKind}`
									: `empty-${shownKind}-${search ? "search" : "none"}`
						}
						className={fetchingDim(query.isFetching && query.isPlaceholderData)}
					>
						{query.isLoading ? (
							<TermsSkeleton rows={6} />
						) : query.isError ? (
							<LoadErrorState onRetry={() => void query.refetch()} />
						) : items.length ? (
							<ul className="overflow-hidden rounded-2xl border border-border bg-card">
								<AnimatePresence initial={false}>
									{items.map((item) => (
										<TermRow key={item.id} item={item} kind={shownKind as TaxonomyKind} />
									))}
								</AnimatePresence>
							</ul>
						) : (
							<EmptyState icon={Tags} title={search ? "Ничего не нашлось" : "Пока пусто"}>
								{search
									? "Попробуйте другое название или добавьте новое справа."
									: "Добавьте первое значение справа."}
							</EmptyState>
						)}
					</LoadSwap>
				</div>

				<aside className="space-y-3 lg:sticky lg:top-6">
					<form
						className="rounded-2xl border border-border bg-card p-4"
						onSubmit={(event) => {
							event.preventDefault()
							if (draft.trim()) create.mutate()
						}}
					>
						{/* Подпись меняется вместе с классификатором: старая гаснет, новая проявляется, а
						    высота карточки тянется к новой. */}
						<LoadSwap loading={false} id={kind.value}>
							<p className="font-medium">Добавить {kind.one}</p>
							<p className="mt-1 text-sm text-muted-foreground">{kind.hint}</p>
						</LoadSwap>
						<Input
							value={draft}
							onChange={(event) => setDraft(event.target.value)}
							placeholder="Название"
							aria-label="Название"
							className="mt-3"
						/>
						<Button
							type="submit"
							className="mt-2 w-full"
							disabled={!draft.trim()}
							isLoading={create.isPending}
						>
							<Plus />
							Добавить
						</Button>
					</form>
					<div className="rounded-2xl border border-border bg-card p-4 text-sm">
						<p className="mb-2 font-medium">Что стоит проверить</p>
						<p className="text-muted-foreground">
							{unused
								? `${unused} не используются ни в одном материале.`
								: "Все значения используются."}
						</p>
						<p className="mt-1 text-muted-foreground">
							{noIcon ? `${noIcon} без иконки: в каталоге будет заглушка.` : "У всех есть иконки."}
						</p>
						<p className="mt-3 text-xs text-muted-foreground">
							Иконки: JPEG, PNG, WebP или SVG до 1 МБ. Нажмите на иконку, чтобы заменить.
						</p>
					</div>
				</aside>
			</div>
		</PageShell>
	)
}
